import PropTypes from "prop-types";
import { useTheme } from "@hooks/useTheme";
import useEscapeKey from "@hooks/useEscapeKey";

const ConfirmProcessingModal = ({
  isOpen,
  onClose,
  onConfirm,
  onReuseScan,
  onNewScan,
  title = "Duplicate File Warning",
  message = "This file has already been uploaded before. Do you want to proceed with the processing?",
  confirmText = "Process",
  cancelText = "Cancel",
  reuseText = "Reuse previous scan results",
  newScanText = "Perform a new scan",
  "data-testid": dataTestId = "confirm-processing-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  // Determine if we're using the new 3-button mode or legacy 2-button mode
  const isThreeButtonMode = onReuseScan && onNewScan;

  const handleReuse = () => {
    if (onReuseScan) {
      onReuseScan();
    }
    onClose();
  };

  const handleNewScan = () => {
    if (onNewScan) {
      onNewScan();
    }
    onClose();
  };

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-50 flex items-center justify-start md:justify-center ${colors.bgBlurModal} backdrop-blur-sm overflow-x-auto p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div
        data-testid={dataTestId}
        className={`${colors.bgModal} w-[512px] min-w-[512px] overflow-hidden rounded-lg shadow-xl`}
      >
        {/* Header */}
        <div className={`border-b px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <h2 id="modal-title" className={`${colors.textPrimary} text-lg font-semibold`} data-testid={`${dataTestId}-title`}>
            {title}
          </h2>
        </div>

        {/* Content */}
        <div className={`px-6 py-4 ${colors.textSecondary}`} data-testid={`${dataTestId}-content`}>
          <p id="modal-description" className="text-sm" data-testid={fieldValueTestId("message")}>
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end space-x-3 border-t px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <button
            data-testid={`${dataTestId}-cancel-button`}
            onClick={onClose}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${colors.borderOutLineButton} ${colors.textActive} ${colors.bgPrimary} hover:${colors.bgSecondary}`}
          >
            {cancelText}
          </button>
          {isThreeButtonMode ? (
            <>
              <button
                data-testid={`${dataTestId}-reuse-button`}
                onClick={handleReuse}
                className="rounded-lg bg-gradient-to-r from-[#1EC6FF] to-[#367FFF] px-4 py-2 text-sm font-medium transition-colors hover:bg-gradient-to-l hover:from-[#1EC6FF] hover:to-[#367FFF]"
              >
                {reuseText}
              </button>
              <button
                data-testid={`${dataTestId}-new-scan-button`}
                onClick={handleNewScan}
                className="rounded-lg bg-gradient-to-r from-[#14EFFF] to-[#1EC6FF] px-4 py-2 text-sm font-medium transition-colors hover:bg-gradient-to-l hover:from-[#14EFFF] hover:to-[#1EC6FF]"
              >
                {newScanText}
              </button>
            </>
          ) : (
            <button
              data-testid={`${dataTestId}-confirm-button`}
              onClick={onConfirm}
              className="rounded-lg bg-gradient-to-r from-[#1EC6FF] to-[#367FFF] px-4 py-2 text-sm font-medium transition-colors hover:bg-gradient-to-l hover:from-[#1EC6FF] hover:to-[#367FFF]"
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ConfirmProcessingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onReuseScan: PropTypes.func,
  onNewScan: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  reuseText: PropTypes.string,
  newScanText: PropTypes.string,
};

export default ConfirmProcessingModal;
